import { UserAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { getErrorMessage } from "../utils/errorMessages";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../translations";
import "../styles/auth.css";

const Signin = () => {
  const { googleSignIn, emailSignIn, forgotPassword, user, loading } = UserAuth();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const T = translations[lang].signin;
  const Tg = translations[lang];

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");

  const handleGoogleSignIn = async () => {
    try {
      setError("");
      await googleSignIn();
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    try {
      setError("");
      await emailSignIn(email, password);
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      setError("");
      await forgotPassword(forgotPasswordEmail);
      setMessage(T.resetPasswordSent);
      setShowForgotPassword(false);
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (user != null) {
      const isEmailPasswordUser = user.providerData &&
        user.providerData.some(provider => provider.providerId === 'password');

      if (isEmailPasswordUser && !user.emailVerified) {
        navigate("/verify-email");
      } else {
        const currentSearchParams = new URLSearchParams(window.location.search);
        const preservedParams = currentSearchParams.toString();
        const redirectTo = `${preservedParams ? `/?${preservedParams}` : "/"}`;
        navigate(redirectTo);
      }
    }
  }, [user, navigate]);

  if (loading) {
    return <div className="loading">{Tg.loading}</div>;
  }

  return (
    <div className="auth-container">
      <h1 className="auth-title">{T.title}</h1>

      {error && (
        <div className="alert alert-error">
          {error}
          {(error.includes("No account found") || error.includes("Invalid email or password")) && (
            <div style={{ marginTop: "8px" }}>
              <span>{T.noAccountExists} </span>
              <Link to="/signup" style={{ color: "inherit", textDecoration: "underline" }}>
                {T.clickToCreate}
              </Link>
            </div>
          )}
        </div>
      )}
      {message && <div className="alert alert-success">{message}</div>}

      {!showForgotPassword ? (
        <>
          <div className="google-signin">
            <button className="gsi-material-button" onClick={handleGoogleSignIn} type="button">
              <div className="gsi-material-button-state"></div>
              <div className="gsi-material-button-content-wrapper">
                <div className="gsi-material-button-icon">
                  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" xmlnsXlink="http://www.w3.org/1999/xlink" style={{display: 'block'}}>
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                    <path fill="none" d="M0 0h48v48H0z"></path>
                  </svg>
                </div>
                <span className="gsi-material-button-contents">{T.continueWithGoogle}</span>
                <span style={{display: 'none'}}>{T.continueWithGoogle}</span>
              </div>
            </button>
          </div>

          <div className="divider">
            <span>{T.or}</span>
          </div>

          <form onSubmit={handleEmailSignIn} className="auth-form">
            <div className="form-group">
              <input
                type="email"
                placeholder={T.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
              />
            </div>
            <div className="form-group">
              <input
                type="password"
                placeholder={T.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="form-input"
              />
            </div>
            <button type="submit" className="btn btn-primary">
              {T.signInWithEmail}
            </button>
          </form>

          <div className="auth-links">
            <button
              onClick={() => setShowForgotPassword(true)}
              className="btn-link"
            >
              {T.forgotPassword}
            </button>
          </div>
          <div className="auth-links">
            <span>{T.noAccount} </span>
            <Link to="/signup">{T.signUp}</Link>
          </div>
          <div className="auth-links">
            <p>{T.googleNote}<br /> {T.googleNoteEmail}</p>
          </div>
        </>
      ) : (
        <>
          <form onSubmit={handleForgotPassword} className="auth-form">
            <h2 className="auth-subtitle">{T.resetTitle}</h2>
            <p className="auth-subtitle">{T.resetSubtitle}</p>

            <div className="form-group">
              <input
                type="email"
                placeholder={T.emailPlaceholder}
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                required
                className="form-input"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ marginBottom: "10px" }}
            >
              {T.sendResetEmail}
            </button>

            <button
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="btn btn-secondary"
            >
              {T.backToSignIn}
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default Signin;
